<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  props: {
    modules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      errors: {},
      id: null,
      name: '',
      permissions: {},
      salesOrderRole: null,
      purchaseOrderRole: null,
    };
  },
  computed: {
    hasSalesOrderModuleAccess(){
     const salesOrderModule = this.modules.find(m => m.name === 'Sales_Order');
     if (!salesOrderModule) return false;
     return this.hasPermission(salesOrderModule.id, 'read');
    },
    
    isSalesOrderReadEnabled(){
     const salesOrderModule = this.modules.find(m => m.name === 'Sales_Order');
     if (!salesOrderModule || !this.permissions[salesOrderModule.id]) return false;
     return this.permissions[salesOrderModule.id].read;
    },
    
    salesOrderModuleId() {
     return this.modules.find(m => m.name === 'Sales_Order')?.id;
    },

    hasPurchaseOrderModuleAccess(){
   const purchaseOrderModule = this.modules.find(m => m.name === 'Purchase_Order');
   if (!purchaseOrderModule) return false;
   return this.hasPermission(purchaseOrderModule.id, 'read');
  },

  isPurchaseOrderReadEnabled(){
   const purchaseOrderModule = this.modules.find(m => m.name === 'Purchase_Order');
   if (!purchaseOrderModule || !this.permissions[purchaseOrderModule.id]) return false;
   return this.permissions[purchaseOrderModule.id].read;
  },

  purchaseOrderModuleId() {
   return this.modules.find(m => m.name === 'Purchase_Order')?.id;
  }
  },
  watch: {
   // ✅ Watch permissions changes and reset when Sales Order read becomes false
   'permissions': {
    handler(newPermissions) {
     const salesOrderId = this.salesOrderModuleId;
      if(salesOrderId && newPermissions[salesOrderId] && !newPermissions[salesOrderId].read) {
       this.salesOrderRole = null;
      }
      const purchaseOrderId = this.purchaseOrderModuleId;
    if(purchaseOrderId && newPermissions[purchaseOrderId] && !newPermissions[purchaseOrderId].read) {
     this.purchaseOrderRole = null;
    }
    },
    deep: true
   }
  },

  methods: {
   hasPermission(moduleId, operation) {
    if (!this.auth || this.auth.type === 'SUPER_ADMIN') return true;
    const perms = this.auth.role?.permissions || [];
    const modulePerm = perms.find(p => p.module_id === moduleId);
    return modulePerm ? modulePerm[operation] : false;
   },

    // ✅ NEW: Reset sales order role when read is unchecked
   onSalesOrderReadChange(event) {
    if (!event.target.checked && this.salesOrderModuleId) {
     this.salesOrderRole = null;
    }
   },

   initPermissions() {
    this.permissions = {};
    this.modules.forEach(module => {
     this.permissions[module.id] = {
      create: false,
      read: false,
      update: false,
      delete: false,
     };
    });
   },

   formatPermissions() {
    return Object.entries(this.permissions).map(([module_id, perms]) => ({
      module_id: parseInt(module_id),
      ...perms,
    }));
   },

   getSalesOrderFlags() {
    return {
      is_sales_order_approver: this.salesOrderRole === 'approver',
      is_sales_order_production: this.salesOrderRole === 'production',
      is_sales_order_drawing: this.salesOrderRole === 'drawing',
      is_sales_order_cutting: this.salesOrderRole === 'cutting',
      is_sales_order_bending: this.salesOrderRole === 'bending',
      is_sales_order_fabrication: this.salesOrderRole === 'fabrication',
      is_sales_order_powder_coating: this.salesOrderRole === 'powder_coating',
      is_sales_order_packaging: this.salesOrderRole === 'packaging',
      is_sales_order_dispatch: this.salesOrderRole === 'dispatch',
    };
   },

   getPurchaseOrderFlags() {
    return {
      is_purchase_order_approver: this.purchaseOrderRole === 'approver',
    };
    },

   add() {
    this.resetForm();
    this.initPermissions();
   },

   editRole(role) {
    this.resetForm();
    this.id = role.id;
    this.name = role.name;
      
     if(role.is_sales_order_approver){
      this.salesOrderRole = 'approver';
    } else if(role.is_sales_order_production){
      this.salesOrderRole = 'production';
    } else if(role.is_sales_order_drawing){  
      this.salesOrderRole = 'drawing';
    } else if(role.is_sales_order_cutting){
      this.salesOrderRole = 'cutting';
    } else if(role.is_sales_order_bending){
      this.salesOrderRole = 'bending';
    } else if(role.is_sales_order_fabrication){
      this.salesOrderRole = 'fabrication';
    } else if(role.is_sales_order_powder_coating){
      this.salesOrderRole = 'powder_coating';
    } else if(role.is_sales_order_packaging){
      this.salesOrderRole = 'packaging';
    }  else if(role.is_sales_order_dispatch){
      this.salesOrderRole = 'dispatch';
    }
     if(role.is_purchase_order_approver){
      this.purchaseOrderRole = 'approver';
    }
      
     this.initPermissions();

     if(Array.isArray(role.permissions)){
      role.permissions.forEach((p) => {
       if(this.permissions[p.module_id]){
        this.permissions[p.module_id].create = p.create;
        this.permissions[p.module_id].read = p.read;
        this.permissions[p.module_id].update = p.update;
        this.permissions[p.module_id].delete = p.delete;
       }
      });
     }
    },

    async addUpdateRole() {
     this.errors = {};
     this.setLoading(true);

     const payload = {
      name: this.name,
      permissions: this.formatPermissions(),
      ...this.getSalesOrderFlags(),
      ...this.getPurchaseOrderFlags(),
     };

     try {
      let response;
      if(this.id){
       response = await axios.put('/roles/update', {
        id: this.id,
        ...payload,
       });
      }else{
       response = await axios.post('/roles/create', payload);
      }

     this.$swalToast(response.data.message);
     this.$emit('updated');
     this.$closeModal('role_permission_modal');
     this.resetForm();
    }catch(error){
     if(error.type === 'validation'){
      this.errors = error.errors;
     }else{
      this.$swalToast(error.message, 'error');
     }
    }finally{
     this.setLoading(false);
    }
   },

   async deleteRole() {
    this.setLoading(true);
     try{
      const response = await axios.delete(`/roles/${this.id}/delete`);
      this.$swalToast(response.data.message);
      this.$emit('updated');
      this.$closeModal('delete_modal');
     }catch(error){
      this.$swalToast(error.message, 'error');
     }finally{
      this.setLoading(false);
     }
    },

    resetForm() {
     this.id = null;
     this.name = '';
     this.errors = {};
     this.permissions = {};
     this.salesOrderRole = null;
     this.purchaseOrderRole = null;
    },
  },
};
</script>

<template>
  <!-- Role Add/Edit Modal -->
  <div class="modal fade" id="role_permission_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg w-100">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ id ? 'Edit' : 'Add' }} Role</h4>
          <button type="button" class="btn-close custom-btn-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="ti ti-x"></i>
          </button>
        </div>
        <form @submit.prevent="addUpdateRole">
          <div class="modal-body pb-0">
            <div class="row">
              <div class="col-md-5">
                <div class="mb-3">
                  <label class="form-label">Role Name</label>
                  <input type="text" class="form-control" v-model="name" />
                  <div v-if="errors.name" class="text-danger small mt-1">{{ errors.name[0] }}</div>
                </div>
              </div>
            </div>

            <!-- Sales Order Section with Read Checkbox -->
            <div class="row mb-3" v-if="hasSalesOrderModuleAccess">
            <div class="col-md-12">

            <!-- Radio Buttons - Show only when read is checked -->
            <div class="row g-3" v-if="isSalesOrderReadEnabled">
            <div class="col-md-4">
             <div class="form-check">
              <input class="form-check-input" type="radio" id="approver" value="approver" v-model="salesOrderRole">
              <label class="form-check-label" for="approver">Sales Order Approver</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="radio" id="drawing" value="drawing" v-model="salesOrderRole">
                <label class="form-check-label" for="drawing">Sales Order Drawing</label>
              </div>
            </div>
            <div class="col-md-4">
             <div class="form-check">
              <input class="form-check-input" type="radio" id="production" value="production" v-model="salesOrderRole">
              <label class="form-check-label" for="production">Sales Order Production</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="radio" id="cutting" value="cutting" v-model="salesOrderRole">
                <label class="form-check-label" for="cutting">Sales Order Cutting</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="radio" id="bending" value="bending" v-model="salesOrderRole">
                <label class="form-check-label" for="bending">Sales Order Bending</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="radio" id="fabrication" value="fabrication" v-model="salesOrderRole">
                <label class="form-check-label" for="fabrication">Sales Order Fabrication</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="radio" id="powder_coating" value="powder_coating" v-model="salesOrderRole">
                <label class="form-check-label" for="powder_coating">Sales Order Powder Coating</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="radio" id="packaging" value="packaging" v-model="salesOrderRole">
                <label class="form-check-label" for="packaging">Sales Order Packaging</label>
              </div>
            </div>
            <div class="col-md-4">
              <div class="form-check">
                <input class="form-check-input" type="radio" id="dispatch" value="dispatch" v-model="salesOrderRole">
                <label class="form-check-label" for="dispatch">Sales Order Dispatch</label>
              </div>
            </div>
            </div>
            </div>
            </div>

            <!-- Purchase Order Section with Read Checkbox -->
            <div class="row mb-3" v-if="hasPurchaseOrderModuleAccess">
              <div class="col-md-12">
                <div class="row g-3" v-if="isPurchaseOrderReadEnabled">
                  <div class="col-md-4">
                    <div class="form-check">
                      <input class="form-check-input" type="radio" id="po_approver" value="approver" v-model="purchaseOrderRole">
                      <label class="form-check-label" for="po_approver">Purchase Order Approver</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>

    <!-- Permissions Table -->
    <div class="row">
    <div class="col-lg-12">
    <table class="table mb-0" v-if="modules.length">
    <thead>
      <tr>
        <th>#</th>
        <th>Module</th>
        <th>Create</th>
        <th>Read</th>
        <th>Update</th>
        <th>Delete</th>
      </tr>
    </thead>
    <tbody>
     <tr v-for="(module, index) in modules" :key="module.id">
      <td>{{ index + 1 }}</td>
      <td>{{ module.name }}</td>
      <td>
      <input type="checkbox" :checked="permissions[module.id]?.create || false" @change="permissions[module.id].create = $event.target.checked" :disabled="!hasPermission(module.id, 'create')"/>
      </td>
      <td>
       <input type="checkbox" :checked="permissions[module.id]?.read || false" @change="permissions[module.id].read = $event.target.checked" :disabled="!hasPermission(module.id, 'read')"/>
      </td>
      <td>
       <input type="checkbox" :checked="permissions[module.id]?.update || false" @change="permissions[module.id].update = $event.target.checked" :disabled="!hasPermission(module.id, 'update')"/>
      </td>
      <td>
       <input type="checkbox" :checked="permissions[module.id]?.delete || false" @change="permissions[module.id].delete = $event.target.checked" :disabled="!hasPermission(module.id, 'delete')"/>
      </td>
      </tr>
     </tbody>
    </table>
    </div>
    </div>
    </div>

    <div class="modal-footer">
     <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
     <button type="submit" class="btn btn-primary">Save</button>
    </div>
    </form>
    </div>
    </div>
    </div>

  <!-- Delete Modal -->
  <div class="modal fade" id="delete_modal">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-body text-center">
          <span class="avatar avatar-xl bg-transparent-danger text-danger mb-3">
            <i class="ti ti-trash-x fs-36"></i>
          </span>
          <h4 class="mb-1">Confirm Delete</h4>
          <p class="mb-3">
            You want to delete the item, this can't be undone once deleted.
          </p>
          <div class="d-flex justify-content-center">
            <a href="javascript:void(0);" class="btn btn-light me-3" data-bs-dismiss="modal">Cancel</a>
            <a href="javascript:void(0);" class="btn btn-danger" @click="deleteRole">Yes, Delete</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
